import { useEffect, useMemo, useState } from "react";
import { Layout } from "../../layouts";
import { useAuth, useCart, useOrderOption, useStore } from "../../hooks";
import { GetStoreMenu, UpdateCart } from "../../services";
import { Toast } from "../../components/Toast";
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Row,
} from "react-bootstrap";
import { BiEnvelope, BiPhone, BiShieldQuarter } from "react-icons/bi";
import { formatDate } from "../../utilities";
import { StoreProductModel } from "../../models";
import { OrderType } from "../../enums";
import { useGiftcard, useShipping } from "../../context";
import dayjs from "dayjs";
import { CustomerForm } from "./customer-form";
import { RecipientForm } from "./recipient-form";
import { useNavigate } from "react-router-dom";
import "./giftcard.css";
import { GiftcardForm } from "./giftcard-form";
import Skeleton from "react-loading-skeleton";

export const GiftcardPurchasePage = () => {
  const navigate = useNavigate();
  const { selectedStore } = useStore();

  const [isLoading, setIsLoading] = useState(false);
  const {
    quantity,
    setQuantity,
    modifierId,
    setModifierId,
    selectedModifiers,
    setSelectedModifiers,
    resetGiftcardForm,
  } = useGiftcard();

  const [activeStep, setActiveStep] = useState<
    "giftcard" | "customer" | "recipient"
  >("giftcard");

  const { customerId, customer, setCustomer, isLoggedIn, deviceId } = useAuth();
  const { storeId, giftcardMenu, setGiftcardMenu, setStoreGateways } =
    useStore();
  const { cartId, setCartId, setMenuId, cartItems, setCartItems } = useCart();
  const { orderOption, setOrderOption } = useOrderOption();
  const { shippingLocation, setShippingLocation } = useShipping();

  useEffect(() => {
    if (storeId && !giftcardMenu) fetchMenu();
  }, [storeId, giftcardMenu]);

  const fetchMenu = async () => {
    setIsLoading(true);

    try {
      const { status, data, message } = await GetStoreMenu({
        store_id: storeId,
        menu_id: "giftcard",
      });

      if (status) {
        setGiftcardMenu(data);
      } else {
        Toast.error(message);
      }
    } catch (e) {
      Toast.error("Unable to fetch giftcard menu");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = (product: StoreProductModel) => {
    if (selectedModifiers.length === 0) {
      Toast.error("Please select a giftcard amount");
      return;
    }

    setMenuId(giftcardMenu?.menu_id || "");
    setCartItems([
      {
        product_id: product.id,
        product_name: product.product_name,
        product_price: product.product_price,
        product_qty: quantity,
        modifier_group: selectedModifiers,
      },
    ]);

    const dateString = dayjs().add(2, "hour").toISOString();

    setOrderOption({
      ...orderOption,
      type_id: OrderType.GIFTCARD,
      order_date: formatDate(dateString, "DD/MM/YYYY"),
      order_time: formatDate(dateString, "hh:mmA"),
      tip_status: "0",
    });

    setActiveStep("customer");
  };

  const modifierMap = useMemo(() => {
    const map = new Map();

    giftcardMenu?.product.forEach((product) => {
      product.modifier_group.forEach((group) => {
        group.modifier.forEach((modifier) => {
          map.set(modifier.m_id, {
            product,
            group,
            modifier,
          });
        });
      });
    });

    return map;
  }, [giftcardMenu]);

  const handleModifierChange = (modifierId: string) => {
    setModifierId(modifierId);

    const selected = modifierMap.get(modifierId);

    if (!selected) return;

    const { group: modifierGroup, modifier } = selected;

    const modifierObj = {
      m_id: modifier.m_id,
      m_name: modifier.m_name,
      m_price: Number(modifier.m_price),
      m_qty: Number(modifier.m_qty) ?? 1,
    };

    setSelectedModifiers((prev) => {
      const groupIndex = prev.findIndex((g) => g.g_id === modifierGroup.g_id);

      // Group doesn't exist → add it
      if (groupIndex === -1) {
        return [
          ...prev,
          {
            g_id: modifierGroup.g_id,
            g_name: modifierGroup.g_name,
            g_parent_id: modifierGroup.g_parent_id,
            modifiers: [modifierObj],
          },
        ];
      }

      const updated = [...prev];
      const group = updated[groupIndex];

      const existingIndex = group.modifiers.findIndex(
        (m) => m.m_id === modifierObj.m_id,
      );

      let modifiers = [...group.modifiers];

      if (modifierGroup.g_max_selection === 1) {
        modifiers = [modifierObj];
      } else if (existingIndex === -1) {
        modifiers.push(modifierObj);
      } else {
        modifiers[existingIndex] = modifierObj;
      }

      updated[groupIndex] = {
        ...group,
        modifiers,
      };

      return updated;
    });
  };

  const giftCardAmount = useMemo(() => {
    return (
      quantity *
        selectedModifiers.reduce(
          (sum, group) =>
            sum +
            group.modifiers.reduce((sub, mod) => {
              return sub + Number(mod.m_price);
            }, 0),
          0,
        ) || 0
    );
  }, [quantity, selectedModifiers]);

  const handleCustomer = (data: any) => {
    setCustomer({
      ...customer,
      first_name: data.firstname,
      last_name: data.lastname,
      email: data.email,
      phone: data.phone,
    });

    setActiveStep("recipient");
  };

  const handleRecipient = async (formData: any) => {
    const updatedLocation = {
      ...shippingLocation,
      id: "",
      first_name: formData.firstname,
      last_name: formData.lastname,
      name: (formData.firstname + " " + formData.lastname).trim(),
      email: formData.email,
      phone: formData.phone,
      address_street: formData.street_address,
      latitude: "",
      longitude: "",
      state_code: formData.state,
      city_name: formData.city,
      zip_code: formData.postal_code,
    };

    setShippingLocation(updatedLocation);

    setIsLoading(true);

    try {
      const { status, message, data } = await UpdateCart({
        store_id: storeId,
        device_id: deviceId,
        customer_id: isLoggedIn ? customerId : "0",
        cart_id: cartId,
        cart: cartItems,
        order_option: orderOption,
        shipping: updatedLocation,
      });

      if (status) {
        setCartId(data.cart_id);
        setCartItems(data.cart);
        setStoreGateways(data.gateway);
        resetGiftcardForm();
        navigate("/checkout");
      } else {
        Toast.error(message);
      }
    } catch (error) {
      Toast.error("An error occurred while creating the cart");
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuantityChange = (quantity: number) => {
    setQuantity(quantity);
  };

  return (
    <Layout pageTitle="Giftcard" showCartIcon={false} showLoader={isLoading}>
      <Container>
        <div className="giftcard-stepper mb-4">
          <div
            className={`giftcard-step ${
              activeStep === "giftcard" ? "active" : "completed"
            }`}
          >
            <div className="giftcard-step-circle">1</div>
            <span>Gift Card</span>
          </div>

          <div className="giftcard-step-line"></div>

          <div
            className={`giftcard-step ${
              activeStep === "customer"
                ? "active"
                : activeStep === "recipient"
                  ? "completed"
                  : ""
            }`}
          >
            <div className="giftcard-step-circle">2</div>
            <span>Personal Details</span>
          </div>

          <div className="giftcard-step-line"></div>

          <div
            className={`giftcard-step ${
              activeStep === "recipient" ? "active" : ""
            }`}
          >
            <div className="giftcard-step-circle">3</div>
            <span>Recipient Details</span>
          </div>
        </div>

        {activeStep === "giftcard" && isLoading && (
          <Card className="transparent-bg shadow-none border-none">
            <CardBody className="p-4 giftcard-wrapper">
              <Row>
                <Col xs={12} md={6}>
                  <Skeleton height={250} className="rounded" />
                </Col>
                <Col xs={12} md={6}>
                  <Skeleton height={250} className="rounded" />
                </Col>
              </Row>
            </CardBody>
          </Card>
        )}

        {activeStep === "giftcard" && (
          <>
            {giftcardMenu?.product.map((product) => (
              <GiftcardForm
                key={product.id}
                product={product}
                store={selectedStore}
                amount={giftCardAmount}
                selected={modifierId}
                quantity={quantity}
                onQuantityChange={handleQuantityChange}
                onModifierChange={handleModifierChange}
                onSubmit={handleClick}
              />
            ))}
          </>
        )}

        {activeStep === "customer" && (
          <Card className="gift-step-card border-0 mb-4">
            <CardBody>
              <CardTitle className="text-uppercase card-title h5 mt-2 mb-3">
                Personal Details
              </CardTitle>
              <CustomerForm
                onBack={() => setActiveStep("giftcard")}
                onSubmit={handleCustomer}
              />
            </CardBody>
          </Card>
        )}

        {activeStep === "recipient" && (
          <Card className="gift-step-card border-0">
            <CardBody>
              <CardTitle className="text-uppercase card-title h5 mt-2 mb-3">
                Recipient Details
              </CardTitle>
              <RecipientForm
                onBack={() => setActiveStep("customer")}
                onSubmit={handleRecipient}
              />
            </CardBody>
          </Card>
        )}

        <div className="giftcard-trust-strip">
          <div className="giftcard-trust-item">
            <div className="giftcard-trust-icon">
              <BiShieldQuarter size={20} />
            </div>
            <div>
              <strong>Secure Payment</strong>
              <span>100% safe & secure</span>
            </div>
          </div>

          <div className="giftcard-trust-item">
            <div className="giftcard-trust-icon">
              <BiPhone size={20} />
            </div>
            <div>
              <strong>Call us</strong>
              <span>
                <a href={`tel:${selectedStore?.store_phone}`}>
                  {selectedStore?.store_phone}
                </a>
              </span>
            </div>
          </div>

          <div className="giftcard-trust-item">
            <div className="giftcard-trust-icon">
              <BiEnvelope size={20} />
            </div>
            <div>
              <strong>Email us</strong>
              <span>
                <a href={`mailto:${selectedStore?.store_email}`}>
                  {selectedStore?.store_email}
                </a>
              </span>
            </div>
          </div>
        </div>
      </Container>
    </Layout>
  );
};
